if (!customElements.get('sey-highlights')) {
  customElements.define(
    'sey-highlights',
    class SeyHighlights extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.track = this.querySelector('[data-highlights-track]');
        this.slides = Array.from(this.querySelectorAll('[data-highlights-slide]'));
        this.nextButton = this.querySelector('[data-highlights-next]');
        this.dots = Array.from(this.querySelectorAll('[data-highlights-dot]'));
        this.index = 0;
        this.scrolling = false;
        this.raf = 0;

        if (!this.track || this.slides.length < 2) return;

        this.go = this.go.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onScrollEnd = this.onScrollEnd.bind(this);
        this.onResize = this.onResize.bind(this);

        this.nextButton?.addEventListener('click', () => this.go(1));
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => {
            const next = Number(dot.dataset.index);
            if (Number.isNaN(next) || next === this.index) return;
            this.index = next;
            this.animateTo(this.scrollTarget(next));
            this.update();
          });
        });
        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.track.addEventListener('scrollend', this.onScrollEnd);
        window.addEventListener('resize', this.onResize);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onResize);
        this.cancelAnimate();
      }

      go(delta) {
        const total = this.slides.length;
        const next = (this.index + delta + total) % total;
        if (next === this.index) return;

        this.index = next;
        this.animateTo(this.scrollTarget(next));
        this.update();
      }

      animateTo(target) {
        const start = this.track.scrollLeft;
        const change = target - start;
        if (Math.abs(change) < 1) return;

        this.cancelAnimate();
        this.scrolling = true;

        const duration = Math.min(900, Math.max(420, Math.abs(change) * 0.45));
        const startTime = performance.now();
        const ease = (t) => 1 - Math.pow(1 - t, 3);

        const step = (now) => {
          const t = Math.min(1, (now - startTime) / duration);
          this.track.scrollLeft = start + change * ease(t);
          if (t < 1) {
            this.raf = requestAnimationFrame(step);
            return;
          }
          this.track.scrollLeft = target;
          this.raf = 0;
          this.scrolling = false;
        };

        this.raf = requestAnimationFrame(step);
      }

      cancelAnimate() {
        if (this.raf) {
          cancelAnimationFrame(this.raf);
          this.raf = 0;
        }
        this.scrolling = false;
      }

      scrollTarget(index) {
        const slide = this.slides[index];
        if (!slide) return 0;
        if (index === 0) return 0;

        const trackRect = this.track.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const slideLeft = slideRect.left - trackRect.left + this.track.scrollLeft;
        const target = slideLeft - (trackRect.width - slideRect.width) / 2;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        return Math.max(0, Math.min(maxScroll, target));
      }

      nearestIndex() {
        const current = this.track.scrollLeft;
        let nearest = 0;
        let distance = Number.POSITIVE_INFINITY;
        this.slides.forEach((_, index) => {
          const delta = Math.abs(this.scrollTarget(index) - current);
          if (delta < distance) {
            distance = delta;
            nearest = index;
          }
        });
        return nearest;
      }

      onScroll() {
        if (this.scrolling) return;
        const next = this.nearestIndex();
        if (next === this.index) return;
        this.index = next;
        this.update();
      }

      onScrollEnd() {
        if (this.scrolling) return;
        const next = this.nearestIndex();
        this.index = next;
        this.update();
        const target = this.scrollTarget(next);
        if (Math.abs(this.track.scrollLeft - target) > 2) {
          this.animateTo(target);
        }
      }

      onResize() {
        this.cancelAnimate();
        this.track.scrollLeft = this.scrollTarget(this.index);
      }

      update() {
        this.dots.forEach((dot, index) => {
          const active = index === this.index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }
    }
  );
}
